/**
 * NumberTicker Component
 * 
 * Animated number counter for KPI values.
 * Per design.md: Subtle motion, opacity and transform only.
 * 
 * Adapted from Magic UI patterns for Upzora.
 */

import { useEffect, useRef, useState } from 'react';
import { useInView, motion, useSpring, useTransform } from 'framer-motion';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { cn } from '@uprosal/ui';

interface NumberTickerProps {
    value: number;
    direction?: 'up' | 'down';
    delay?: number;
    className?: string;
    decimalPlaces?: number;
    suffix?: string;
    prefix?: string;
}

export function NumberTicker({
    value,
    direction = 'up',
    delay = 0,
    className,
    decimalPlaces = 0,
    suffix = '',
    prefix = '',
}: NumberTickerProps) {
    const ref = useRef<HTMLSpanElement>(null);
    const isInView = useInView(ref, { once: true, margin: '-50px' });
    const prefersReducedMotion = useReducedMotion();
    const [hasAnimated, setHasAnimated] = useState(false);

    const springValue = useSpring(direction === 'down' ? value : 0, {
        bounce: 0,
        duration: 1500,
    });

    const displayValue = useTransform(springValue, (current) =>
        Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        }).format(Number(current.toFixed(decimalPlaces)))
    );

    useEffect(() => {
        if (isInView && !hasAnimated) {
            const timeout = setTimeout(() => {
                springValue.set(direction === 'down' ? 0 : value);
                setHasAnimated(true);
            }, delay * 1000);
            return () => clearTimeout(timeout);
        }
    }, [isInView, delay, springValue, value, direction, hasAnimated]);

    // If user prefers reduced motion, show static value
    if (prefersReducedMotion) {
        return (
            <span className={className}>
                {prefix}{value.toLocaleString()}{suffix}
            </span>
        );
    }

    return (
        <span ref={ref} className={cn('tabular-nums tracking-tight', className)}>
            {prefix}
            <motion.span>{displayValue}</motion.span>
            {suffix}
        </span>
    );
}
